import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  addCredits,
  deleteAgent,
  deleteIntegrationSettingByUuid,
  deleteMeetingApi,
  deleteParticipant,
  deleteSettingByUuid,
  deleteUserApi,
  fetchAllAgents,
  fetchAllMeetings,
  fetchAllParticipants,
  fetchAllTransactions,
  fetchMeetingByUuid,
  getAllIntegrationSettings,
  getAllSettings,
  getAllUserActivityAPI,
  getTaskByIdApi,
  getUsers,
} from "./adminAPI";

// AGENTS
export const getAllAgents = createAsyncThunk(
  "admin/agents",
  async (_, thunkAPI) => {
    try {
      return await fetchAllAgents();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// INTEGRATION SETTINGS
export const fetchAllIntegrationSettings = createAsyncThunk(
  "admin/integrationSettings",
  async (_, thunkAPI) => {
    try {
      return await getAllIntegrationSettings();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// MEETINGS
export const getAllMeetings = createAsyncThunk(
  "admin/meetings",
  async (_, thunkAPI) => {
    try {
      return await fetchAllMeetings();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// PARTICIPANTS
export const getAllParticipants = createAsyncThunk(
  "admin/participants",
  async (_, thunkAPI) => {
    try {
      return await fetchAllParticipants();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// SETTINGS
export const fetchAllSettingDetails = createAsyncThunk(
  "admin/settings",
  async (_, thunkAPI) => {
    try {
      return await getAllSettings();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// TRANSACTIONS
export const getAllTransactions = createAsyncThunk(
  "admin/transactions",
  async (_, thunkAPI) => {
    try {
      return await fetchAllTransactions();
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// USERS
export const fetchUsers = createAsyncThunk(
  "admin/users",
  async (_, thunkAPI) => {
    try {
      const res = await getUsers();
      return res.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ACTIVITY
export const getAllUserActivity = createAsyncThunk(
  "admin/activity",
  async (_, thunkAPI) => {
    try {
      const res = await getAllUserActivityAPI();
      return res.data.activities;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// ADD CREDITS
export const addCreditToUser = createAsyncThunk(
  "admin/addCredits",
  async (data, thunkAPI) => {
    try {
      return await addCredits(data);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// SINGLE MEETING
export const getMeetingByUuid = createAsyncThunk(
  "admin/getMeetingByUuid",
  async (meetingId, thunkAPI) => {
    try {
      return await fetchMeetingByUuid(meetingId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// TASKS
export const getTaskById = createAsyncThunk(
  "admin/getTaskById",
  async (meetingId, thunkAPI) => {
    try {
      return await getTaskByIdApi(meetingId);
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// DELETE MEETING
export const deleteMeeting = createAsyncThunk(
  "admin/deleteMeeting",
  async (meetingId, thunkAPI) => {
    try {
      await deleteMeetingApi(meetingId);
      return meetingId;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// DELETE AGENT
export const removeAgent = createAsyncThunk(
  "admin/removeAgent",
  async (uuid, thunkAPI) => {
    try {
      await deleteAgent(uuid);
      return uuid;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// DELETE INTEGRATION
export const deleteIntegrationSetting = createAsyncThunk(
  "admin/deleteIntegration",
  async ({ uuid }, thunkAPI) => {
    try {
      await deleteIntegrationSettingByUuid(uuid);
      return uuid;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// DELETE PARTICIPANT
export const removeParticipant = createAsyncThunk(
  "admin/removeParticipant",
  async (uuid, thunkAPI) => {
    try {
      await deleteParticipant(uuid);
      return uuid;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

// DELETE SETTING
export const deleteSetting = createAsyncThunk(
  "admin/deleteSetting",
  async ({ uuid }, thunkAPI) => {
    try {
      await deleteSettingByUuid(uuid);
      return uuid;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

export const deleteUser = createAsyncThunk(
  "admin/deleteUser",
  async (uuid, thunkAPI) => {
    try {
      await deleteUserApi(uuid);
      return uuid;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data || error.message);
    }
  },
);

const initialState = {
  loading: false,
  error: null,
  agents: [],
  settings: [],
  integrationsSetting: [],
  meetings: [],
  participants: [],
  payments: [],
  transactions: [],
  users: [],
  activity: [],
  selectedMeeting: null,
  tasks: [],
};

const adminSlice = createSlice({
  name: "admin",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      .addCase(getAllAgents.fulfilled, (s, a) => {
        s.loading = false;
        s.agents = a.payload || [];
      })

      .addCase(fetchAllIntegrationSettings.fulfilled, (s, a) => {
        s.loading = false;
        s.integrationsSetting = a.payload || [];
      })

      .addCase(getAllMeetings.fulfilled, (s, a) => {
        s.loading = false;
        s.meetings = a.payload || [];
      })

      .addCase(getAllParticipants.fulfilled, (s, a) => {
        s.loading = false;
        s.participants = a.payload?.data || [];
      })

      .addCase(fetchAllSettingDetails.fulfilled, (s, a) => {
        s.loading = false;
        s.settings = a.payload || [];
      })

      .addCase(getAllTransactions.fulfilled, (s, a) => {
        s.payments = a.payload?.payments || [];
        s.transactions = a.payload?.transactions || [];
      })

      .addCase(fetchUsers.fulfilled, (s, a) => {
        s.loading = false;
        s.users = a.payload;
      })

      .addCase(getAllUserActivity.fulfilled, (s, a) => {
        s.loading = false;
        s.activity = a.payload;
      })

      .addCase(getMeetingByUuid.fulfilled, (s, a) => {
        s.loading = false;
        s.selectedMeeting = a.payload?.data || a.payload;
      })

      .addCase(getTaskById.fulfilled, (s, a) => {
        s.loading = false;
        s.tasks = a.payload?.actions || a.payload;
      })

      .addCase(deleteMeeting.fulfilled, (s, a) => {
        s.loading = false;
        s.meetings = s.meetings.filter((m) => m.uuid !== a.payload);
      })

      .addCase(removeAgent.fulfilled, (s, a) => {
        s.loading = false;
        s.agents = s.agents.filter((x) => x.uuid !== a.payload);
      })

      .addCase(deleteIntegrationSetting.fulfilled, (s, a) => {
        s.loading = false;
        s.integrationsSetting = s.integrationsSetting.filter(
          (x) => x.uuid !== a.payload,
        );
      })

      .addCase(removeParticipant.fulfilled, (s, a) => {
        s.loading = false;
        s.participants = s.participants.filter((x) => x.uuid !== a.payload);
      })

      .addCase(deleteSetting.fulfilled, (s, a) => {
        s.loading = false;
        s.settings = s.settings.filter((x) => x.uuid !== a.payload);
      })

      .addCase(deleteUser.fulfilled, (s, a) => {
        s.loading = false;

        s.users = s.users.map((user) =>
          user.uuid === a.payload
            ? {
                ...user,
                isDeleted: true,
              }
            : user,
        );
      })

      .addMatcher(
        (action) =>
          action.type.startsWith("admin/") && action.type.endsWith("/pending"),
        (state) => {
          state.loading = true;
          state.error = null;
        },
      )

      .addMatcher(
        (action) =>
          action.type.startsWith("admin/") && action.type.endsWith("/rejected"),
        (state, action) => {
          state.loading = false;
          state.error = action.payload;
        },
      );
  },
});

export default adminSlice.reducer;
